import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
type Slide = {
  src: string;
  alt: string;
  title: string;
  caption: string;
};
const slides: Slide[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/h2UU7627uFq8pZoWJqUcVW/Screenshot_20260622-192222~2.png',
  alt: 'La propuesta de valor',
  title: '🎯 LA PROPUESTA DE VALOR',
  caption: 'El mapa de 4 piezas para explicar qué hacés, para quién, desde qué mirada y qué cambio acompañás.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nVwmoQmcxNtywRdNnSB5gf/Screenshot_20260622-193032~2.png',
  alt: 'Mitos frecuentes',
  title: '💡 IDEAS DE CONTENIDO',
  caption: 'Ejemplos prácticos para refutar mitos, educar a tu audiencia y diferenciar tu especialidad.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gNiBGHpGNT9FnBhgLLFg3K/Screenshot_20260622-193145~2.png',
  alt: 'Plantillas de guiones',
  title: '📝 GUIONES PARA REELS',
  caption: 'Estructuras listas para grabar videos que conectan con los dolores y necesidades de tus consultantes.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/upWR7djS9t89NecFbgvZFh/Screenshot_20260622-193236~2.png',
  alt: 'Plantillas de WhatsApp',
  title: '💬 RESPUESTAS DE WHATSAPP',
  caption: 'Mensajes rápidos para copiar y pegar, organizando turnos y respondiendo consultas con profesionalismo.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/w43bEWvHk94USH9o5apDTT/Screenshot_20260622-193415~2.png',
  alt: 'Publicaciones diarias',
  title: '🗓️ CALENDARIO DE PUBLICACIONES',
  caption: '31 ideas de publicaciones para transformar situaciones del día a día en invitaciones terapéuticas.'
}];
export const MaterialPreview = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + slides.length) % slides.length);
  }, [index]);
  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % slides.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setIndex(i => (i - 1 + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next, paused]);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null || slides.length <= 1) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();else prev();
    }
    setTouchStartX(null);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.96
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.96
    })
  };
  return <Section className="max-w-5xl" data-id="element-226">
      <div className="text-center mb-8 md:mb-12" data-id="element-227">
        <div className="inline-flex items-center justify-center bg-white/80 border border-brand-aqua/40 px-4 py-1.5 rounded-full mb-5 shadow-sm" data-id="element-228">
          <span className="font-serif font-semibold tracking-wider text-brand-darkGreen text-xs md:text-sm" data-id="element-229">
            📸 VISTA PREVIA DEL MATERIAL
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold leading-tight text-brand-darkGreen" data-id="element-230">
          Conocé por dentro el sistema que ya están utilizando cientos de
          terapeutas.
        </h2>
        <p className="text-brand-text/70 mt-4 text-sm md:text-base max-w-2xl mx-auto" data-id="element-231">
          Todo diseñado para ayudarte a comunicar tu valor, atraer a las
          personas correctas y construir una práctica sostenible.
        </p>
      </div>

      <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-232">
        <div className="relative rounded-3xl overflow-hidden border border-brand-aqua/30 bg-gradient-to-br from-brand-darkGreen/95 via-brand-aquaDark/90 to-brand-aqua/90 shadow-[0_20px_60px_rgba(13,148,136,0.25)]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-233">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none" data-id="element-234"></div>

          <div className="relative w-full flex items-center justify-center px-4 md:px-16 py-8 md:py-12" style={{
          minHeight: '420px'
        }} data-id="element-235">
            <AnimatePresence custom={direction} mode="wait" data-id="element-236">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 260,
                damping: 30
              },
              opacity: {
                duration: 0.3
              },
              scale: {
                duration: 0.3
              }
            }} className="flex flex-col items-center justify-center w-full" data-id="element-237">
                <div className="relative" data-id="element-238">
                  <div className="absolute -inset-4 bg-white/20 blur-2xl rounded-3xl" data-id="element-239"></div>
                  <img src={slides[index].src} alt={slides[index].alt} draggable={false} className="relative max-h-[420px] md:max-h-[520px] w-auto max-w-full rounded-xl shadow-2xl ring-1 ring-white/20 select-none" data-id="element-240" />
                </div>
                <div className="relative mt-5 md:mt-6 text-center max-w-md" data-id="element-241">
                  <p className="font-display font-bold text-white text-sm md:text-lg tracking-wide drop-shadow" data-id="element-242">
                    {slides[index].title}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {slides.length > 1 && <>
              <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-aqua/40 shadow-md flex items-center justify-center text-brand-aquaDark hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-243">
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-244" />
              </button>
              <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-aqua/40 shadow-md flex items-center justify-center text-brand-aquaDark hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-245">
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-246" />
              </button>
            </>}
        </div>

        <div className="mt-5 text-center px-4 min-h-[2.5rem]" data-id="element-247">
          <AnimatePresence mode="wait" data-id="element-248">
            <motion.p key={index} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.25
          }} className="text-sm md:text-base font-medium text-brand-darkGreen" data-id="element-249">
              {slides[index].caption}
            </motion.p>
          </AnimatePresence>
        </div>

        {slides.length > 1 && <>
            <div className="flex items-center justify-center gap-2 mt-4" data-id="element-250">
              {slides.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-brand-aqua' : 'w-2 bg-brand-aqua/20 hover:bg-brand-aqua/50'}`} data-id="element-251" />)}
            </div>
            <p className="text-center text-xs text-brand-muted mt-3" data-id="element-252">
              {index + 1} / {slides.length} · Deslizá o usá las flechas
            </p>
          </>}
      </div>
    </Section>;
};